import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCurrency, getBarHeight } from '../utils/salesReportUtils';
import { downloadSalesReport, downloadSettledBills, getSalesReport } from '../api/salesApi';

const buildEmptyReport = () => ({
    summary: { totalSales: 0, gstCollected: 0, cgstCollected: 0, sgstCollected: 0, cash: 0, paytm: 0, upi: 0, zomato: 0, card: 0, complimentary: 0 },
    todaySummary: { totalSales: 0, cash: 0, paytm: 0, upi: 0, zomato: 0, card: 0, complimentary: 0 },
    restaurants: [],
    restaurantComparison: [],
    timelineTrends: [],
    bills: [],
});

const getTrendDate = (value) => {
    const dateValue = String(value || '');
    const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = dateMatch
        ? new Date(Date.UTC(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3])))
        : new Date(value);

    if (Number.isNaN(date.getTime())) return { day: dateValue, full: dateValue };

    return {
        day: new Intl.DateTimeFormat('en-IN', { day: '2-digit', timeZone: 'UTC' }).format(date),
        full: new Intl.DateTimeFormat('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
        }).format(date),
    };
};

const formatCompactCurrency = (value) => {
    const amount = Number(value || 0);
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}k`;
    return `₹${Math.round(amount)}`;
};

const formatTaxCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(Number(value || 0));

export default function SuperAdminSalesReport() {
    const todayStr = new Date().toISOString().split('T')[0];
    const [dateRange, setDateRange] = useState({ start: todayStr, end: todayStr });
    const [selectedRestaurant, setSelectedRestaurant] = useState('all');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [activeModalBill, setActiveModalBill] = useState(null);
    const [showAllInvoices, setShowAllInvoices] = useState(false);
    const [data, setData] = useState(buildEmptyReport());

    const getReport = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await getSalesReport({
                startDate: dateRange.start,
                endDate: dateRange.end,
                restaurantId: selectedRestaurant
            });

            if (!response || response.success === false) {
                throw new Error(response?.message || 'Unable to load the sales report.');
            }

            const emptyReport = buildEmptyReport();
            setData({
                summary: response.summary || emptyReport.summary,
                todaySummary: response.todaySummary || emptyReport.todaySummary,
                restaurants: Array.isArray(response.restaurants) ? response.restaurants : [],
                restaurantComparison: Array.isArray(response.restaurantComparison) ? response.restaurantComparison : [],
                timelineTrends: Array.isArray(response.timelineTrends) ? response.timelineTrends : [],
                bills: Array.isArray(response.bills) ? response.bills : [],
            });
        } catch (err) {
            console.error('Sales report fetch failed', err);
            setError(err?.response?.data?.message || err.message || 'Unable to load the sales report.');
            setData(buildEmptyReport());
        } finally {
            setLoading(false);
        }
    }, [dateRange.start, dateRange.end, selectedRestaurant]);

    useEffect(() => {
        getReport();
    }, [getReport]);

    const handleDownloadReport = async () => {
        setDownloading(true);
        setError('');

        try {
            const response = await downloadSalesReport({
                startDate: dateRange.start,
                endDate: dateRange.end,
                restaurantId: selectedRestaurant,
            });
            const fileUrl = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = `sales-report-${dateRange.start}-to-${dateRange.end}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileUrl);
        } catch (err) {
            console.error('Sales report download failed', err);
            setError(err?.response?.data?.message || 'Unable to download the sales report.');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadSettledBills = async () => {
        setDownloading(true);
        setError('');

        try {
            const response = await downloadSettledBills({
                startDate: dateRange.start,
                endDate: dateRange.end,
                restaurantId: selectedRestaurant,
            });
            const fileUrl = window.URL.createObjectURL(new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = `settled-bills-${dateRange.start}-to-${dateRange.end}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileUrl);
        } catch (err) {
            console.error('Settled bills download failed', err);
            setError(err?.response?.data?.message || 'Unable to download settled bills.');
        } finally {
            setDownloading(false);
        }
    };

    const periodSummary = data.summary;

    const paymentModes = useMemo(() => [
        { key: 'cash', label: 'Cash', amount: Number(periodSummary?.cash || 0) },
        { key: 'paytm', label: 'Paytm', amount: Number(periodSummary?.paytm || 0) },
        { key: 'upi', label: 'UPI', amount: Number(periodSummary?.upi || 0) },
        { key: 'zomato', label: 'Zomato', amount: Number(periodSummary?.zomato || 0) },
        { key: 'card', label: 'Card', amount: Number(periodSummary?.card || 0) },
        { key: 'complimentary', label: 'Complimentary', amount: Number(periodSummary?.complimentary || 0) },
    ], [periodSummary]);

    const totalTodayValue = paymentModes.reduce((sum, mode) => sum + mode.amount, 0);
    const gstCollectedCents = Math.round(Number(data.summary?.gstCollected || 0) * 100);
    const splitCgstCents = Math.floor(gstCollectedCents / 2);
    const cgstCollected = data.summary?.cgstCollected ?? splitCgstCents / 100;
    const sgstCollected = data.summary?.sgstCollected ?? (gstCollectedCents - splitCgstCents) / 100;
    const maxOutletTotal = useMemo(() => Math.max(...data.restaurantComparison.map((outlet) => Number(outlet.total || 0)), 1), [data.restaurantComparison]);
    const maxTimelineSales = useMemo(() => Math.max(...data.timelineTrends.map((entry) => Number(entry.sales || 0)), 1), [data.timelineTrends]);
    const timelineLabelStep = Math.max(1, Math.ceil(data.timelineTrends.length / 6));
    const peakTimelineEntry = data.timelineTrends.reduce(
        (peak, entry) => Number(entry.sales || 0) > Number(peak?.sales || 0) ? entry : peak,
        null
    );
    const hasTimelineSales = data.timelineTrends.some((entry) => Number(entry.sales || 0) > 0);

    const summaryCards = [
        { label: 'Selected period', value: data.summary.totalSales, tone: 'from-indigo-500 to-violet-500' },
        { label: 'Today captured', value: data.todaySummary.totalSales, tone: 'from-emerald-500 to-lime-500' },
        { label: 'Bills settled', value: data.bills.length, tone: 'from-sky-500 to-cyan-500' },
        { label: 'Average ticket', value: data.bills.length ? data.summary.totalSales / data.bills.length : 0, tone: 'from-amber-500 to-orange-500' },
    ];

    return (
        <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-6">
            <div className="mx-auto flex min-w-0 max-w-7xl flex-col gap-6">
                <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-2xl shadow-slate-950/40 backdrop-blur">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">Super admin</p>
                            <h1 className="mt-1 text-3xl font-semibold text-white">Sales report dashboard</h1>
                            <p className="mt-2 max-w-2xl text-sm text-slate-400">
                                Review a selected day or date range, compare restaurant performance, and inspect payment-wise receipts in one place.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <select
                                value={selectedRestaurant}
                                onChange={(e) => setSelectedRestaurant(e.target.value)}
                                className="rounded-2xl border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm font-medium text-slate-200 outline-none"
                            >
                                <option value="all">All outlets</option>
                                {data.restaurants.map((restaurant) => (
                                    <option key={restaurant.id} value={String(restaurant.id)}>
                                        {restaurant.name}
                                    </option>
                                ))}
                            </select>

                            <div className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-950/70 p-2">
                                <input
                                    type="date"
                                    value={dateRange.start}
                                    onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                                    className="bg-transparent text-sm text-slate-200 outline-none"
                                />
                                <span className="text-slate-500">to</span>
                                <input
                                    type="date"
                                    value={dateRange.end}
                                    onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                                    className="bg-transparent text-sm text-slate-200 outline-none"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={getReport}
                                disabled={loading || downloading}
                                className="rounded-2xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-400"
                            >
                                {loading ? 'Refreshing...' : 'Refresh'}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadReport}
                                disabled={downloading || loading}
                                className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {downloading ? 'Preparing Excel...' : 'Download Sales Report'}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadSettledBills}
                                disabled={downloading || loading}
                                className="rounded-2xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {downloading ? 'Preparing Excel...' : 'Download Settled Bills'}
                            </button>
                        </div>
                    </div>
                </div>

                {error ? (
                    <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">
                        {error}
                    </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {summaryCards.map((card) => (
                        <div key={card.label} className="rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20">
                            <div className={`h-1.5 rounded-full bg-gradient-to-r ${card.tone}`} />
                            <p className="mt-4 text-sm font-medium text-slate-400">{card.label}</p>
                            <p className="mt-2 text-2xl font-semibold text-white">
                                {typeof card.value === 'number' && card.label !== 'Bills settled' ? formatCurrency(card.value) : card.value}
                            </p>
                        </div>
                    ))}
                </div>

                <section
                    aria-labelledby="gst-collections-heading"
                    className="min-w-0 rounded-3xl border border-emerald-500/20 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20"
                >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Tax collection</p>
                            <h2 id="gst-collections-heading" className="mt-1 text-lg font-semibold text-white">GST collected</h2>
                        </div>
                        <p className="text-xs text-slate-400">
                            Settlement date: {dateRange.start === dateRange.end ? dateRange.start : `${dateRange.start} to ${dateRange.end}`}
                        </p>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-live="polite">
                        <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                            <div className="flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-sky-400" aria-hidden="true" />
                                <p className="text-sm font-medium text-slate-300">CGST</p>
                            </div>
                            <p className="mt-2 text-2xl font-semibold tabular-nums text-white">
                                {loading ? 'Loading...' : formatTaxCurrency(cgstCollected)}
                            </p>
                        </div>
                        <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                            <div className="flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-violet-400" aria-hidden="true" />
                                <p className="text-sm font-medium text-slate-300">SGST</p>
                            </div>
                            <p className="mt-2 text-2xl font-semibold tabular-nums text-white">
                                {loading ? 'Loading...' : formatTaxCurrency(sgstCollected)}
                            </p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.08] p-4">
                            <p className="text-sm font-medium text-emerald-200">Total GST</p>
                            <p className="mt-2 text-2xl font-semibold tabular-nums text-emerald-300">
                                {loading ? 'Loading...' : formatTaxCurrency(data.summary.gstCollected)}
                            </p>
                        </div>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">GST is calculated from bill item rates and allocated to payments settled during the selected range.</p>
                </section>

                <div className="grid min-w-0 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                    <div className="min-w-0 rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">Payment details</p>
                                <h2 className="mt-1 text-xl font-semibold text-white">Amount received in range</h2>
                            </div>
                            <div className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-300">
                                {dateRange.start === dateRange.end ? 'Single day' : 'Selected range'}
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {paymentModes.map((mode) => (
                                <div key={mode.key} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-slate-300">{mode.label}</p>
                                        <span className="text-xs uppercase tracking-[0.25em] text-slate-500">
                                            {totalTodayValue ? `${Math.round((mode.amount / totalTodayValue) * 100)}%` : '0%'}
                                        </span>
                                    </div>
                                    <p className="mt-3 text-2xl font-semibold text-white">{formatCurrency(mode.amount)}</p>
                                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400"
                                            style={{ width: `${totalTodayValue ? (mode.amount / totalTodayValue) * 100 : 0}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="min-w-0 rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20">
                        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">Restaurant comparison</p>
                        <h2 className="mt-1 text-xl font-semibold text-white">Sales by outlet</h2>

                        <div className="mt-5 space-y-4">
                            {data.restaurantComparison.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-700 p-4 text-sm text-slate-400">
                                    No outlet sales available for the selected window.
                                </div>
                            ) : (
                                data.restaurantComparison.map((outlet) => (
                                    <div key={outlet.r_id} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-medium text-slate-200">{outlet.restaurant_name || outlet.r_id}</span>
                                            <span className="font-semibold text-white">{formatCurrency(outlet.total)}</span>
                                        </div>
                                        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-800">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 to-indigo-500"
                                                style={{ width: `${getBarHeight(outlet.total, maxOutletTotal)}%` }}
                                            />
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-400">
                                            <span>Cash {formatCurrency(outlet.cash)}</span>
                                            <span>Digital {formatCurrency(outlet.digital)}</span>
                                            <span>Bills {outlet.bills}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid min-w-0 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                    <div className="min-w-0 rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">Daily trend</p>
                                <h2 className="mt-1 text-xl font-semibold text-white">Sales over the selected period</h2>
                            </div>
                            {peakTimelineEntry && hasTimelineSales ? (
                                <div className="text-right">
                                    <p className="text-xs text-slate-400">Peak day</p>
                                    <p className="text-sm font-semibold text-cyan-300">
                                        {formatCompactCurrency(peakTimelineEntry.sales)}
                                        <span className="ml-1 font-normal text-slate-400">{getTrendDate(peakTimelineEntry.date).full}</span>
                                    </p>
                                </div>
                            ) : null}
                        </div>

                        <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/70 p-3 sm:p-4">
                            {data.timelineTrends.length === 0 ? (
                                <div className="flex h-52 items-center justify-center text-sm text-slate-400">
                                    No daily trend is available yet for this filter.
                                </div>
                            ) : !hasTimelineSales ? (
                                <div className="flex h-52 flex-col items-center justify-center gap-2 text-center">
                                    <p className="text-sm font-medium text-slate-300">No sales recorded in this period</p>
                                    <p className="text-xs text-slate-500">Daily sales will appear here when bills are settled.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-start gap-3">
                                        <div className="flex h-48 w-12 shrink-0 flex-col justify-between pb-1 text-right text-[10px] text-slate-500" aria-hidden="true">
                                            <span>{formatCompactCurrency(maxTimelineSales)}</span>
                                            <span>{formatCompactCurrency(maxTimelineSales / 2)}</span>
                                            <span>₹0</span>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="relative h-48 border-b border-slate-700">
                                                <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden="true">
                                                    <span className="border-t border-slate-800" />
                                                    <span className="border-t border-dashed border-slate-800" />
                                                    <span className="border-t border-slate-800" />
                                                </div>
                                                <div className="absolute inset-0 flex items-end gap-1 px-0.5 sm:gap-1.5 sm:px-1">
                                                    {data.timelineTrends.map((entry) => {
                                                        const sales = Number(entry.sales || 0);
                                                        const trendDate = getTrendDate(entry.date);
                                                        const barHeight = sales > 0
                                                            ? Math.max(2, (sales / maxTimelineSales) * 100)
                                                            : 0;

                                                        return (
                                                            <div
                                                                key={entry.date}
                                                                className="group flex h-full min-w-0 flex-1 items-end"
                                                                title={`${trendDate.full}: ${formatCurrency(sales)}`}
                                                                aria-label={`${trendDate.full}: ${formatCurrency(sales)}`}
                                                            >
                                                                <div
                                                                    className="w-full rounded-t-sm bg-gradient-to-t from-indigo-600 to-cyan-300 transition-[height,filter] duration-200 group-hover:brightness-125 sm:rounded-t-md"
                                                                    style={{ height: `${barHeight}%` }}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div
                                                className="grid pt-2 text-center text-[10px] text-slate-400 sm:text-xs"
                                                style={{ gridTemplateColumns: `repeat(${data.timelineTrends.length}, minmax(0, 1fr))` }}
                                            >
                                                {data.timelineTrends.map((entry, index) => {
                                                    const showDate = index === 0
                                                        || index === data.timelineTrends.length - 1
                                                        || index % timelineLabelStep === 0;

                                                    return (
                                                        <span key={entry.date} className="min-w-0 truncate">
                                                            {showDate ? getTrendDate(entry.date).day : ''}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-3 text-center text-[11px] text-slate-500">Daily sales in INR · Hover a bar for the exact date and amount</p>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="min-w-0 rounded-3xl border border-slate-800 bg-slate-900/80 p-5 shadow-lg shadow-slate-950/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">Invoices</p>
                                <h2 className="mt-1 text-xl font-semibold text-white">Recent settled bills</h2>
                            </div>
                            <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-1 text-sm text-slate-400">
                                {data.bills.length} found
                            </span>
                        </div>

                        <div className={"mt-5 space-y-3 " + (showAllInvoices ? "max-h-[420px] overflow-y-auto pr-1" : "")}>
                            {loading ? (
                                <div className="rounded-2xl border border-dashed border-slate-700 p-4 text-sm text-slate-400">
                                    Loading sales data...
                                </div>
                            ) : data.bills.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-700 p-4 text-sm text-slate-400">
                                    No bills match the current filter.
                                </div>
                            ) : (
                                (showAllInvoices ? data.bills : data.bills.slice(0, 6)).map((bill) => (
                                    <button
                                        key={bill.bill_id}
                                        type="button"
                                        onClick={() => setActiveModalBill(bill)}
                                        className="flex w-full items-center justify-between rounded-2xl border border-slate-800 bg-slate-950/70 px-4 py-3 text-left transition hover:border-indigo-500/50"
                                    >
                                        <div>
                                            <p className="font-semibold text-white">#{bill.bill_id}</p>
                                            <p className="text-sm text-slate-400">{bill.seating_name || 'Table'} • {bill.customer_name || 'Walk-in'}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-white">{formatCurrency(bill.total_amount)}</p>
                                            <p className="text-sm text-slate-400">{bill.dateofbill}</p>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        {data.bills.length > 6 && (
                            <div className="mt-4 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setShowAllInvoices((prev) => !prev)}
                                    className="rounded-2xl border border-slate-700 bg-slate-950/80 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-900"
                                >
                                    {showAllInvoices ? 'Show less' : `Show ${data.bills.length - 6} more`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {activeModalBill ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
                    <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl shadow-slate-950/40">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">Invoice details</p>
                                <h3 className="mt-1 text-xl font-semibold text-white">#{activeModalBill.bill_id}</h3>
                                <p className="mt-1 text-sm text-slate-400">{activeModalBill.dateofbill} • {activeModalBill.restaurant_name}</p>
                            </div>
                            <button type="button" onClick={() => setActiveModalBill(null)} className="rounded-full border border-slate-700 p-2 text-xl text-slate-300">
                                ×
                            </button>
                        </div>

                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                                <p className="text-sm font-semibold text-slate-300">Payment breakdown</p>
                                <div className="mt-3 space-y-2 text-sm text-slate-400">
                                    {Object.entries(activeModalBill.payment_breakdown || {}).map(([channel, value]) => (
                                        <div key={channel} className="flex items-center justify-between">
                                            <span className="capitalize">{channel}</span>
                                            <span className="font-medium text-slate-200">{formatCurrency(value)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                                <p className="text-sm font-semibold text-slate-300">Bill summary</p>
                                <div className="mt-3 space-y-2 text-sm text-slate-400">
                                    <div className="flex items-center justify-between">
                                        <span>Table / seating</span>
                                        <span className="font-medium text-slate-200">{activeModalBill.seating_name || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>Customer</span>
                                        <span className="font-medium text-slate-200">{activeModalBill.customer_name || 'Walk-in'}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>Grand total</span>
                                        <span className="font-semibold text-white">{formatCurrency(activeModalBill.total_amount)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                            <p className="text-sm font-semibold text-slate-300">Items</p>
                            <div className="mt-3 space-y-2 text-sm text-slate-400">
                                {activeModalBill.items?.length ? (
                                    activeModalBill.items.map((item, index) => (
                                        <div key={`${item.name}-${index}`} className="flex items-center justify-between">
                                            <span>{item.name} × {item.quantity}</span>
                                            <span className="font-medium text-slate-200">{formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p>No item details were returned for this bill.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}